import {
    GetPaperPlanMetadataOutput,
    PaperPlanMetadata,
    RateGroupsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { CorePlan, PaperPlanGroup, PaperPlanRateGroup } from 'src/constants';
import { IPlanValues } from 'src/types/IPaperPlan';
import { IFirstPartyCorePlan, IPlanNameToMetadataObj } from 'src/types/IRateCard';
import {
    buildFirstPartyCorePlanFrom,
    getCorePlan,
    getPlanAttributes,
    getPlanGroup,
    getRateGroup,
    isCorePlan
} from 'src/util/PaperPlans/PaperPlanUtils';

/*
 * Build a lookup object and core/1P/3P plan list using getPaperPlanMetadata API response
 * lookup object: key is the paperPlanName, value is an object containing required attribute/metadata for 1P/3P/full rate screens
 */
export const buildPlanMetadataLookup = (planMetadataResponse: GetPaperPlanMetadataOutput) => {
    const planMetadataLookupObj = {} as IPlanNameToMetadataObj;
    const corePlanList = [] as IFirstPartyCorePlan[];
    const firstPartyPlanList = [] as Record<string, string | boolean | number>[];
    const thirdPartyPlanList = [] as Record<string, string | boolean | number>[];
    const firstPartyFreeTrialPlanList = [] as Record<string, string | boolean | number>[];
    const thirdPartyFreeTrialPlanList = [] as Record<string, string | boolean | number>[];

    planMetadataResponse.planMetadata?.forEach((plan: PaperPlanMetadata) => {
        const { paperPlan, attributeSet } = plan;
        if (
            !paperPlan?.isDraft &&
            !paperPlan?.isDeleted &&
            !planMetadataLookupObj[paperPlan?.paperPlanName as string]
        ) {
            const attributeObj = {
                ...getPlanAttributes(attributeSet?.[0].attributes || []),
                paperPlanName: paperPlan?.paperPlanName
            } as IPlanValues;

            const rateGroup = getRateGroup(attributeObj);
            const planGroup = getPlanGroup(attributeObj);

            const planObj = {
                paperPlanId: paperPlan?.paperPlanId as number,
                displayName: paperPlan?.displayName || '',
                paperPlanName: paperPlan?.paperPlanName || '',
                rateGroup,
                planGroup,
                corePlan: getCorePlan(attributeObj),
                territory: paperPlan?.territory || '',
                endUserPrice: attributeObj.endUserPrice || '',
                wholesalePrice: attributeObj.wholesalePrice || '',
                thirdPartyCurrency: attributeObj.endUserPriceCurrency || attributeObj.wholesalePriceCurrency || '',
                planType: attributeObj.planType,
                customerPays: attributeObj.customerPays,
                isPromotion: attributeObj.isPromotion,
                renewalCadence: attributeObj.renewalCadence || '',
                promoDurationInDays: attributeObj.promoDurationInDays || '',
                isBundle: attributeObj.isBundle
            };

            // Add 90 Day FT or HD 90 Day FT or Montana 90 Day FT as possible 1P plan
            if (
                [RateGroupsEnum.ONE_P_PROMO, RateGroupsEnum.NON_MONTHLY].includes(rateGroup as RateGroupsEnum) ||
                [
                    CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL,
                    CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT,
                    CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL
                ].includes(paperPlan?.paperPlanName as CorePlan)
            ) {
                firstPartyPlanList.push(planObj);
            } else if (
                [RateGroupsEnum.CARRIER_INTRO, RateGroupsEnum.CARRIER_STANDARD].includes(rateGroup as RateGroupsEnum)
            ) {
                thirdPartyPlanList.push(planObj);
            } else if (isFirstPartyFreeTrial(planGroup, rateGroup)) {
                firstPartyFreeTrialPlanList.push(planObj);
            } else if (isThirdPartyFreeTrial(planGroup, rateGroup)) {
                thirdPartyFreeTrialPlanList.push(planObj);
            }

            planMetadataLookupObj[paperPlan?.paperPlanName as string] = planObj;
        }

        if (paperPlan && isCorePlan(paperPlan)) {
            corePlanList.push(buildFirstPartyCorePlanFrom(paperPlan));
        }
    });

    return {
        planMetadataLookupObj,
        corePlanList,
        firstPartyPlanList,
        thirdPartyPlanList,
        firstPartyFreeTrialPlanList,
        thirdPartyFreeTrialPlanList
    };
};

export const isFirstPartyFreeTrial = (planGroup: PaperPlanGroup | '', rateGroup: PaperPlanRateGroup | '') => {
    return planGroup === PaperPlanGroup.FreeTrial && rateGroup === PaperPlanRateGroup.FreeTrial;
};

export const isThirdPartyFreeTrial = (planGroup: PaperPlanGroup | '', rateGroup: PaperPlanRateGroup | '') => {
    return planGroup === PaperPlanGroup.CarrierFreeTrial && rateGroup === PaperPlanRateGroup.FreeTrial;
};
